import { z } from 'zod';

const NonEmpty = z.string().min(1);

export const VersionStatus = z.enum([
  'streaming',
  'complete',
  'partial',
  'error',
  'cancelled',
]);
export type VersionStatus = z.infer<typeof VersionStatus>;

export const ProjectSchema = z.object({
  id: NonEmpty,
  name: NonEmpty,
  autoNamed: z.boolean(),
  createdAt: z.number(),
  updatedAt: z.number(),
  currentVersionId: z.string().nullable(),
  thumbnailDataUrl: z.string().optional(),
});
export type Project = z.infer<typeof ProjectSchema>;

export const VersionSchema = z.object({
  id: NonEmpty,
  projectId: NonEmpty,
  createdAt: z.number(),
  parentVersionId: z.string().nullable(),
  status: VersionStatus,
  htmlPartial: z.string(),
  htmlFinal: z.string().nullable(),
  modelId: NonEmpty,
  promptTurnId: NonEmpty,
  errorMessage: z.string().optional(),
  tokenUsage: z
    .object({ input: z.number(), output: z.number() })
    .optional(),
});
export type Version = z.infer<typeof VersionSchema>;

const SelectorTarget = z.object({
  selector: NonEmpty,
  snippet: z.string(),
  commentId: z.string().optional(),
});
const PendingEdit = z.object({
  selector: NonEmpty,
  newText: z.string(),
});

export const MessageSchema = z.object({
  id: NonEmpty,
  projectId: NonEmpty,
  versionId: z.string().nullable(),
  role: z.enum(['user', 'assistant', 'system']),
  createdAt: z.number(),
  content: z.string(),
  attachments: z
    .object({
      selectorTargets: z.array(SelectorTarget).optional(),
      pendingEdits: z.array(PendingEdit).optional(),
    })
    .optional(),
});
export type Message = z.infer<typeof MessageSchema>;

export const CommentStatus = z.enum(['draft', 'sent', 'stale']);
export type CommentStatus = z.infer<typeof CommentStatus>;

export const CommentSchema = z.object({
  id: NonEmpty,
  projectId: NonEmpty,
  versionId: NonEmpty,
  selector: NonEmpty,
  snippet: z.string(),
  text: z.string(),
  status: CommentStatus,
  createdAt: z.number(),
  sentInMessageId: z.string().optional(),
});
export type Comment = z.infer<typeof CommentSchema>;

export const EditSchema = z.object({
  id: NonEmpty,
  projectId: NonEmpty,
  versionId: NonEmpty,
  selector: NonEmpty,
  oldText: z.string(),
  newText: z.string(),
  createdAt: z.number(),
});
export type Edit = z.infer<typeof EditSchema>;

export const TweakValueSchema = z.object({
  id: NonEmpty,
  projectId: NonEmpty,
  versionId: NonEmpty,
  variable: NonEmpty,
  value: z.string(),
  updatedAt: z.number(),
});
export type TweakValue = z.infer<typeof TweakValueSchema>;

export const SavedFileSchema = z.object({
  id: NonEmpty,
  name: z.string(),
  html: z.string(),
  sourceProjectId: z.string().nullable(),
  sourceVersionId: z.string().nullable(),
  createdAt: z.number(),
  thumbnailDataUrl: z.string().optional(),
});
export type SavedFile = z.infer<typeof SavedFileSchema>;

export const SettingSchema = z.discriminatedUnion('id', [
  z.object({ id: z.literal('apiKey'), value: z.string() }),
  z.object({ id: z.literal('modelId'), value: z.string() }),
  z.object({ id: z.literal('maxTokens'), value: z.number() }),
  z.object({ id: z.literal('tailwindCdnUrl'), value: z.string() }),
  z.object({ id: z.literal('fontCdnAllowlist'), value: z.array(z.string()) }),
]);
export type Setting = z.infer<typeof SettingSchema>;
export type SettingId = Setting['id'];
